import { Icon } from 'components/Icon';
import Spacer from 'components/UI/Layout/Spacer';
import StyledButton from 'components/UI/Button/StyledButton';
import { useHistory } from 'react-router-dom';
import { FlexBoxColumn, FlexBoxRow, FlexBoxDynamic } from 'components/UI/Layout/FlexBox';
import React, { useState, useEffect, SyntheticEvent } from 'react';
import { connect, useDispatch } from 'react-redux';
import analyticsService, { AmplitudeEventData, AnalyticsEvent } from 'services/AnalyticsService';
import { IconSize, FontSize, FontWeight, Spacing } from 'modules/styles/variables';
import { MuiBox, MuiCheckbox } from 'theme/material-ui';
import { CloseBox, Img, RetakeText, ImageBox, TopLeft, Spin } from '../styled';
import { RootState } from 'store/types';
import { UPDATE_CHECKLIST_PHOTO_ID } from 'services/orchestration/mutations';
import { UPDATE_PRE_REGISTRATION_CHECKLIST } from 'store/preRegistration/actions';
import {
  currentLocationPathNameSelector,
  previousLocationPathNameSelector
} from 'store/router/selectors';
import {
  PreRegistrationVisitDetailSelector,
  PreRegistrationFetchingSelector,
  PreRegistrationPatientInfoSelector,
  PreRegistrationCheckListStepByIdSelector
} from 'store/preRegistration/selectors';
import SpinnerModal from 'components/UI/Spinner/SpinnerModal';
import DefaultText from 'components/UI/Typography/DefaultText';
import CameraButton from 'components/UI/Button/CameraButton';
import { Color } from 'modules/styles/colors';
import { PreRegUploadPhotoText, errors, MAX_FILE_SIZE, MAX_DIMENSIONS } from '../constant';
import { CheckListItem, VisitDetail, PatientInfo } from 'store/preRegistration/types';
import { useMutation } from '@apollo/react-hooks';
import { CheckListItemPageProps } from '../Insurance/types';
import { getAge } from 'lib/preRegistration';
import { headerWithJWT, handleError, handleVerifyUpload } from '../utils';
import { FileNameWithImage } from '../types';
import { PreRegHeader } from '../common';

export function validPhotoIds(age: number | null): string {
  let rtn = '';
  if (age === null) {
    rtn = '';
  } else if (age < 5) {
    rtn = PreRegUploadPhotoText.noActionRequired;
  } else if (age < 18) {
    rtn = PreRegUploadPhotoText.under18;
  } else {
    rtn = PreRegUploadPhotoText.over18;
  }

  return rtn;
}

export function composePaylod(
  step: CheckListItem,
  visitDetail: VisitDetail,
  patientInfo: PatientInfo,
  payload: {
    frontFile?: FileNameWithImage;
    backFile?: FileNameWithImage;
  }
) {
  const { frontFile, backFile } = payload;
  const images = [];
  if (!!frontFile?.azureFileName) {
    images.push({
      name: 'Photo ID',
      description: 'Card front',
      azureFileName: frontFile.azureFileName
    });
  }
  if (!!backFile?.azureFileName) {
    images.push({
      name: 'Photo ID',
      description: 'Card back',
      azureFileName: backFile.azureFileName
    });
  }
  const fin = visitDetail.finNumber;
  const mrn = patientInfo.id;
  const aliasId = step.docInfo.docId;

  return {
    arType: visitDetail.arType,
    visitType: visitDetail.type,
    checkListStepId: step.id,
    facilityCode: visitDetail.facilityCode,
    fin,
    mrn,
    stepData: {
      mrn,
      fin,
      aliasId,
      isInsuranceCard: false,
      images
    }
  };
}

export const UploadPhotoIdComponent = (props: CheckListItemPageProps) => {
  const { isLoading, patientInfo, step, visitDetail, currentUrl, referringUrl } = props;
  const [updateChecklistPhotoId, { loading }] = useMutation(UPDATE_CHECKLIST_PHOTO_ID);
  const history = useHistory();
  const dispatch = useDispatch();

  const [frontFile, setFrontFile] = useState<FileNameWithImage | undefined>();
  const [backFile, setBackFile] = useState<FileNameWithImage | undefined>();
  const [skip, setSkip] = useState(false);
  const [verifiedFront, setVerifiedFront] = useState(false);
  const [verifiedBack, setVerifiedBack] = useState(false);

  useEffect(() => {
    if (!step) {
      history.replace('/pre-reg/checklist');
    }
  }, []);

  const age = getAge(patientInfo?.dateOfBirth);

  const handleBackFileVerified = (payload: FileNameWithImage) => {
    setBackFile(payload);
    setVerifiedBack(!!payload?.azureFileName);
  };

  const handleFrontFileVerified = (payload: FileNameWithImage) => {
    setFrontFile(payload);
    setVerifiedFront(!!payload?.azureFileName);
  };

  const backFileUploaded = (newBackFile?: string, filename?: string) =>
    handleVerifyUpload(handleBackFileVerified, dispatch, newBackFile, filename);

  const frontFileUploaded = async (newFrontFile?: string, filename?: string) =>
    handleVerifyUpload(handleFrontFileVerified, dispatch, newFrontFile, filename);

  const onConfirmButtonPress = async () => {
    try {
      if (step.complete) {
        handleError(errors.Complete, history);
      } else if (submitEnabled) {
        const mutationPayload = composePaylod(step, visitDetail, patientInfo, {
          frontFile,
          backFile
        });

        const res = await updateChecklistPhotoId({
          variables: {
            payload: mutationPayload
          },
          context: {
            clientName: 'orchestration',
            headers: headerWithJWT()
          }
        });
        if (res.data.updateChecklistPhotoIDStep.success) {
          dispatch({
            type: UPDATE_PRE_REGISTRATION_CHECKLIST,
            payload: res.data.updateChecklistPhotoIDStep.data
          });

          const eventName = skip
            ? AnalyticsEvent.HospitalFormDetailSkipped
            : AnalyticsEvent.HospitalFormDetailComplete;
          const amplitudeEventData: AmplitudeEventData = {
            currentUrl,
            referringUrl,
            /* eslint-disable-next-line @typescript-eslint/camelcase */
            form_location: visitDetail?.location?.name || '',
            /* eslint-disable-next-line @typescript-eslint/camelcase */
            form_detail_type: `Hospital Registration: ${step.title}`,
            /* eslint-disable-next-line @typescript-eslint/camelcase */
            form_detail_additional_info: `Photo ID Upload Skipped (${skip ? 'Yes' : 'No'})`
          };
          analyticsService.logEvent(eventName, amplitudeEventData);

          history.push('/pre-reg/checklist');
        } else {
          handleError(errors.PhotoID, history);
        }
      }
    } catch (err) {
      handleError(errors.PhotoID, history);
    }
  };

  const submitEnabled = !!(
    (age !== null && age < 5) ||
    skip ||
    (frontFile?.base64 && verifiedFront && !backFile?.base64) ||
    (frontFile?.base64 && verifiedFront && backFile?.base64 && verifiedBack)
  );

  return (
    <FlexBoxColumn
      data-testid="pre-registeration-photoId"
      minHeight="100%"
      spacing={Spacing.large}
      flex="1 1 auto"
    >
      <SpinnerModal isLoading={isLoading || loading} />
      <PreRegHeader title={PreRegUploadPhotoText.title} />

      {age !== null && age >= 5 ? (
        <FlexBoxColumn flex="1 1 auto">
          <MuiBox m={1} p={1}>
            <DefaultText fontSize={FontSize.large} fontWeight={FontWeight.bold}>
              {PreRegUploadPhotoText.description}
            </DefaultText>
          </MuiBox>
          <Spacer size="small" />
          <MuiBox m={1} p={1}>
            <DefaultText fontSize={FontSize.medium}>{validPhotoIds(age)}</DefaultText>
          </MuiBox>

          <FlexBoxRow>
            <FlexBoxDynamic width="50%" justifyContent="center">
              {frontFile?.base64 ? (
                <ImageBox>
                  <TopLeft verified={verifiedFront}>
                    {verifiedFront ? (
                      <Icon name="CheckCircle'" color={Color.green} size={IconSize.small} />
                    ) : (
                      <Spin>
                        <Icon name="cached" color={Color.purple} size={IconSize.small} />
                      </Spin>
                    )}
                  </TopLeft>
                  <CloseBox>
                    <Icon
                      name="close"
                      onClick={() => frontFileUploaded()}
                      size={IconSize.base}
                      color={Color.black}
                    />
                  </CloseBox>
                  <Img src={frontFile?.base64} backgroundColor={Color.grayLight} />
                  <RetakeText textAlign="right" onClick={() => frontFileUploaded()}>
                    Change
                  </RetakeText>
                </ImageBox>
              ) : (
                <MuiBox>
                  <CameraButton
                    data-testid="camera-button-front"
                    id={`UploadPhotoId-front-${Date.now()}`}
                    text="Front of ID"
                    onSuccess={frontFileUploaded}
                    maxFileSize={MAX_FILE_SIZE}
                    maxDimensions={MAX_DIMENSIONS}
                  />
                </MuiBox>
              )}
            </FlexBoxDynamic>
            <FlexBoxDynamic width="50%" justifyContent="center">
              {backFile?.base64 ? (
                <ImageBox>
                  <TopLeft verified={verifiedBack}>
                    {verifiedBack ? (
                      <Icon name="CheckCircle'" color={Color.green} size={IconSize.small} />
                    ) : (
                      <Spin>
                        <Icon name="cached" color={Color.purple} size={IconSize.small} />
                      </Spin>
                    )}
                  </TopLeft>
                  <CloseBox>
                    <Icon
                      name="close"
                      onClick={() => backFileUploaded()}
                      size={IconSize.base}
                      color={Color.black}
                    />
                  </CloseBox>
                  <Img src={backFile?.base64} backgroundColor={Color.grayLight} />
                  <RetakeText onClick={() => backFileUploaded()}>Change</RetakeText>
                </ImageBox>
              ) : (
                <MuiBox>
                  <CameraButton
                    data-testid="camera-button-back"
                    id={`UploadPhotoId-back-${Date.now()}`}
                    text="Back of ID"
                    onSuccess={backFileUploaded}
                    maxFileSize={MAX_FILE_SIZE}
                    maxDimensions={MAX_DIMENSIONS}
                  />
                </MuiBox>
              )}
            </FlexBoxDynamic>
          </FlexBoxRow>
          {!(frontFile || backFile) ? (
            <FlexBoxColumn justifyContent="flex-end" flex="1 1 auto">
              <MuiBox>
                <MuiCheckbox
                  data-testid="skip-adding-photo-checkbox"
                  aria-label="Skip Adding Photo ID"
                  value={skip}
                  onChange={(evt: SyntheticEvent, val: boolean) => setSkip(val)}
                />
                {PreRegUploadPhotoText.skip}
              </MuiBox>
            </FlexBoxColumn>
          ) : null}
        </FlexBoxColumn>
      ) : (
        <DefaultText>{PreRegUploadPhotoText.noActionRequired}</DefaultText>
      )}
      <FlexBoxColumn justifyContent="flex-end">
        <StyledButton
          data-testid="submit-button"
          onPress={onConfirmButtonPress}
          disabled={!submitEnabled}
        >
          Confirm
        </StyledButton>
      </FlexBoxColumn>
    </FlexBoxColumn>
  );
};

const mapStateToProps = (state: RootState, props: CheckListItemPageProps) => {
  const id = props.location.state?.id || '';
  return {
    step: PreRegistrationCheckListStepByIdSelector(id)(state),
    visitDetail: PreRegistrationVisitDetailSelector(state),
    patientInfo: PreRegistrationPatientInfoSelector(state),
    isLoading: PreRegistrationFetchingSelector(state),
    currentUrl: currentLocationPathNameSelector(state),
    referringUrl: previousLocationPathNameSelector(state)
  };
};

export default connect(mapStateToProps)(UploadPhotoIdComponent);
